import modulesConfiguration from './modulesConfiguration';
import { ROLE_GROUPS, Role } from './roles';
import { ENTITIES, getEntityPermissions } from './entityPermissions';

/**
 * Interfaccia per un elemento figlio della sidebar
 */
export interface SidebarChildItem {
	/** Etichetta visualizzata nella sidebar */
	readonly label: string;
	/** Route di destinazione */
	readonly route: string;
	/** Ruoli autorizzati per questo elemento */
	readonly restrictedTo: readonly Role[];
}

/**
 * Interfaccia per un elemento di navigazione sidebar
 */
export interface SidebarItem {
	/** Etichetta visualizzata nella sidebar */
	readonly label: string;
	/** Icona dell'elemento (classe CSS Element UI) */
	readonly icon?: string;
	/** Route di destinazione */
	readonly route?: string;
	/** Ruoli autorizzati per questo elemento */
	readonly restrictedTo: readonly Role[];
	/** Elementi figli (per menu a cascata) */
	readonly children?: readonly SidebarChildItem[];
}

/**
 * Tipo per l'array completo degli elementi sidebar
 */
export type SidebarItems = readonly SidebarItem[];

/**
 * Configurazione base degli elementi della sidebar
 * Questa è la configurazione statica che viene poi estesa dinamicamente
 */
const baseItems: SidebarItem[] = [
	{
		label: 'Dashboard',
		icon: 'el-icon-menu',
		route: '/dashboard',
		restrictedTo: ROLE_GROUPS.EVERYONE,
	},
	{
		label: 'Timecards',
		icon: 'el-icon-time',
		restrictedTo: ROLE_GROUPS.EVERYONE,
		children: [
			{
				label: 'Ricerca',
				route: '/timecard/timecards',
				restrictedTo: getEntityPermissions(ENTITIES.TIMECARD).roles,
			},
			{
				label: 'Calendario',
				route: '/timecard/calendar',
				restrictedTo: getEntityPermissions(ENTITIES.TIMECARD).roles,
			},
			{
				label: 'Per Giorno',
				route: '/timecard/daily',
				restrictedTo: getEntityPermissions(ENTITIES.TIMECARD).roles,
			},
			{
				label: 'Inserimento',
				route: '/timecard/timecards/add',
				restrictedTo: getEntityPermissions(ENTITIES.TIMECARD).roles,
			},
			{
				label: 'Inserimento Multiplo',
				route: '/timecard/multiple',
				restrictedTo: getEntityPermissions(ENTITIES.TIMECARD).roles,
			},
			{
				label: 'Approvazione',
				route: '/timecard/approval',
				restrictedTo: ROLE_GROUPS.MANAGEMENT_LEVEL,
			},
			{
				label: 'Presenze',
				route: '/timecard/attendances',
				restrictedTo: ROLE_GROUPS.SYS_ADMIN_HR,
			},
			{
				label: 'Occupazione',
				route: '/timecard/occupation',
				restrictedTo: ROLE_GROUPS.MANAGEMENT_LEVEL,
			},
			{
				label: 'Pianificazione',
				route: '/timecard/planning',
				restrictedTo: ROLE_GROUPS.MANAGEMENT_LEVEL,
			},
		],
	},
	{
		label: 'Sistema',
		icon: 'el-icon-s-tools',
		restrictedTo: ROLE_GROUPS.MANAGEMENT_LEVEL,
		children: [
			{
				label: 'Utenti',
				route: '/system/users',
				restrictedTo: getEntityPermissions(ENTITIES.USERS).roles,
			},
			{
				label: 'Collaboratori',
				route: '/system/workers',
				restrictedTo: getEntityPermissions(ENTITIES.WORKERS).roles,
			},
			{
				label: 'Gruppi',
				route: '/system/groups',
				restrictedTo: getEntityPermissions(ENTITIES.GROUPS).roles,
			},
			{
				label: 'Clienti',
				restrictedTo: getEntityPermissions(ENTITIES.CUSTOMERS).roles,
				route: '/system/customers',
			},
			{
				label: 'Commesse',
				restrictedTo: getEntityPermissions(ENTITIES.JOB_ORDERS).roles,
				route: '/system/orders',
			},
			{
				label: 'Attività',
				restrictedTo: getEntityPermissions(ENTITIES.ACTIVITIES).roles,
				route: '/system/activities',
			},
			{
				label: 'Centri di Costo',
				restrictedTo: getEntityPermissions(ENTITIES.COST_CENTERS).roles,
				route: '/system/costCenters',
			},
			{
				label: 'Festività',
				restrictedTo: getEntityPermissions(ENTITIES.HOLIDAYS).roles,
				route: '/system/holidays',
			},
			{
				label: 'Integrazioni',
				restrictedTo: ROLE_GROUPS.SYS_ADMIN_ONLY,
				route: '/system/integrations',
			},
		],
	},
];

/**
 * Funzione che costruisce dinamicamente gli elementi della sidebar
 * basandosi sulla configurazione dei moduli abilitati
 * @returns Array completo degli elementi sidebar
 */
function buildSidebarItems(): SidebarItems {
	const items: SidebarItem[] = [...baseItems];

	// Aggiungi condizionalmente il modulo Refunds
	if (modulesConfiguration.refunds) {
		items.push({
			label: 'Rimborsi',
			icon: 'el-icon-document-checked',
			restrictedTo: getEntityPermissions(ENTITIES.REFUNDS).roles,
			children: [
				{
					label: 'Ricerca',
					route: '/refund/refunds',
					restrictedTo: getEntityPermissions(ENTITIES.REFUNDS).roles,
				},
			],
		});
	}

	// Aggiungi condizionalmente il modulo Economics
	if (modulesConfiguration.economics) {
		items.push({
			label: 'Economics',
			icon: 'el-icon-money',
			restrictedTo: getEntityPermissions(ENTITIES.REVENUES).roles,
			children: [
				{
					label: 'Costi/Ricavi',
					restrictedTo: getEntityPermissions(ENTITIES.REVENUES).roles,
					route: '/economics/revenues',
				},
				{
					label: 'Analisi per Anno',
					restrictedTo: ROLE_GROUPS.SYS_ADMIN_ONLY,
					route: '/economics/yearlyAnalisys',
				},
			],
		});
	}

	// Aggiungi condizionalmente il modulo BusinessCentralIntegration
	if (modulesConfiguration.crossCompanyMirroring) {
		items.push({
			label: 'Business Central',
			icon: 'el-icon-office-building',
			restrictedTo: ROLE_GROUPS.SYS_ADMIN_ONLY,
			children: [
				{
					label: 'Commesse Business Central',
					restrictedTo: getEntityPermissions(ENTITIES.PURCHASE_ORDERS).roles,
					route: '/system/purchaseOrders',
				},
				{
					label: 'Profit',
					restrictedTo: getEntityPermissions(ENTITIES.PROFIT_CENTERS).roles,
					route: '/system/profitCenters',
				},
				{
					label: 'Tasks',
					restrictedTo: getEntityPermissions(ENTITIES.TASKS).roles,
					route: '/system/tasks',
				},
			],
		});
	}

	// Aggiungi condizionalmente il modulo CrossCompanyMirroring
	if (modulesConfiguration.crossCompanyMirroring) {
		items.push({
			label: 'Timecard Mirroring',
			icon: 'el-icon-connection',
			restrictedTo: ROLE_GROUPS.SYS_ADMIN_ONLY,
			children: [
				{
					label: 'Configurazione',
					route: '/cross-company/configuration',
					restrictedTo: getEntityPermissions(ENTITIES.MIRRORING_CONFIGURATION).roles,
				},
				{
					label: 'Coda Sincronizzazioni',
					route: '/cross-company/mirroring',
					restrictedTo: getEntityPermissions(ENTITIES.MIRRORING_QUEUE).roles,
				},
			],
		});
	}

	// Aggiungi condizionalmente il modulo AI
	if (modulesConfiguration.ai) {
		items.push({
			label: "Chiedi all'IA",
			icon: 'el-icon-chat-dot-round',
			route: '/ai',
			restrictedTo: ROLE_GROUPS.ADMIN_LEVEL,
		});
	}

	return items;
}

/**
 * Export della configurazione completa degli elementi sidebar
 * Viene costruita dinamicamente in base ai moduli abilitati
 */
const sidebarItems: SidebarItems = buildSidebarItems();

export default sidebarItems;

/**
 * Funzioni di utility per lavorare con gli elementi sidebar
 */

/**
 * Ottiene tutti gli elementi sidebar accessibili da un insieme di ruoli
 * @param userRoles - Ruoli dell'utente
 * @returns Array degli elementi sidebar accessibili
 */
export function getAccessibleSidebarItems(userRoles: readonly string[]): SidebarItems {
	return sidebarItems
		.filter((item) => {
			// Verifica se l'utente può accedere all'elemento principale
			if (!item.restrictedTo.some((role) => userRoles.includes(role))) {
				return false;
			}

			// Se ha figli, filtra anche quelli
			if (item.children) {
				const accessibleChildren = item.children.filter((child) =>
					child.restrictedTo.some((role) => userRoles.includes(role)),
				);
				// Restituisce l'elemento solo se ha almeno un figlio accessibile
				return accessibleChildren.length > 0;
			}

			return true;
		})
		.map((item) => {
			// Se ha figli, filtra quelli accessibili
			if (item.children) {
				const accessibleChildren = item.children.filter((child) =>
					child.restrictedTo.some((role) => userRoles.includes(role)),
				);
				return {
					...item,
					children: accessibleChildren,
				};
			}
			return item;
		});
}

/**
 * Trova un elemento sidebar per route
 * @param route - Route da cercare
 * @returns Elemento sidebar se trovato, undefined altrimenti
 */
export function findSidebarItemByRoute(route: string): SidebarItem | SidebarChildItem | undefined {
	for (const item of sidebarItems) {
		// Controlla l'elemento principale
		if (item.route === route) {
			return item;
		}

		// Controlla gli elementi figli
		if (item.children) {
			const childItem = item.children.find((child) => child.route === route);
			if (childItem) {
				return childItem;
			}
		}
	}

	return undefined;
}

/**
 * Ottiene tutte le route disponibili nella sidebar
 * @returns Array di tutte le route
 */
export function getAllSidebarRoutes(): string[] {
	const routes: string[] = [];

	sidebarItems.forEach((item) => {
		if (item.route) {
			routes.push(item.route);
		}

		if (item.children) {
			item.children.forEach((child) => {
				routes.push(child.route);
			});
		}
	});

	return routes;
}

/**
 * Verifica se una route è presente nella sidebar
 * @param route - Route da verificare
 * @returns true se la route è presente nella sidebar
 */
export function isValidSidebarRoute(route: string): boolean {
	return findSidebarItemByRoute(route) !== undefined;
}

/**
 * Ottiene l'elemento padre di un elemento figlio
 * @param childRoute - Route dell'elemento figlio
 * @returns Elemento padre se trovato, undefined altrimenti
 */
export function getParentSidebarItem(childRoute: string): SidebarItem | undefined {
	for (const item of sidebarItems) {
		if (item.children && item.children.some((child) => child.route === childRoute)) {
			return item;
		}
	}

	return undefined;
}

/**
 * Hook per debugging - stampa tutti gli elementi sidebar
 * @param prefix - Prefisso opzionale per il log
 */
export function logSidebarItems(prefix: string = 'Sidebar Items'): void {
	if (process.env.NODE_ENV !== 'production') {
		console.group(prefix);
		sidebarItems.forEach((item) => {
			console.log(`📂 ${item.label}:`, {
				route: item.route,
				icon: item.icon,
				roles: item.restrictedTo,
				childrenCount: item.children?.length || 0,
			});

			if (item.children) {
				item.children.forEach((child) => {
					console.log(`  └── ${child.label}:`, {
						route: child.route,
						roles: child.restrictedTo,
					});
				});
			}
		});
		console.groupEnd();
	}
}
